<template>
    <div class="financeCalculator">
        <div class="financeCalculator__body">

            <div class="financeCalculator__form">
                <div class="financeCalculator__form__fields">
                    <FinanceCalculatorRow id="financeType" label=" What do you want to finance?">
                        <FinanceCalculatorSelect id="financeType" :options="financeOptions" v-model="params.financeType" />
                    </FinanceCalculatorRow>

                    <FinanceCalculatorRow id="vehicleType" label="Vehicle price">
                        <FinanceCalculatorRange id="vehiclePrice" :min="2000" :max="100000" :default-value="params.price" :formatter="formatCurrency" v-model="params.price" />
                    </FinanceCalculatorRow>

                    <FinanceCalculatorRow id="deposit" label="Deposit amount">
                        <FinanceCalculatorRange id="deposit" :min="0" :max="10000" :step="200" :default-value="params.deposit" :formatter="formatCurrency" v-model="params.deposit" />
                    </FinanceCalculatorRow>

                    <FinanceCalculatorRow id="term" label="Finance term">
                        <FinanceCalculatorRange id="term" :min="1" :max="60" :step="6" :default-value="params.term" :formatter="formatTerms" v-model="params.term" />
                    </FinanceCalculatorRow>

                    <FinanceCalculatorRow id="creditScore" label="Estimated credit score*">
                        <FinanceCalculatorSelect id="creditScore" :options="creditScoreOptions" v-model="params.creditScore" />
                    </FinanceCalculatorRow>
                </div>

                <FinanceCalculatorApprovedMessage parent-class="financeCalculator__approved--form" />
            </div>

            <div class="financeCalculator__finance">
                <FinanceCalculatorQuoteLoading v-if="!isInitiallyFetched" />
                <FinanceCalculatorNotification v-else-if="isError" />
                <FinanceCalculatorQuote :rate="quote.rate"
                                        :term="quote.term"
                                        :total-cost-of-credit="quote.totalCostOfCredit"
                                        :total-funded="quote.totalFunded"
                                        :total-monthly="quote.totalMonthly"
                                        :total-repayment="quote.totalRepayment"
                                        v-else
                />
                <div class="financeCalculator__terms">
                    *This is a representative example only
                </div>

                <button type="button" class="financeCalculator__applyButton" @click="toggleApplicationJourneyOverlay">
                    Apply for finance
                </button>

                <FinanceCalculatorApprovedMessage parent-class="financeCalculator__approved--finance" />

            </div>
        </div>

        <FinanceApplicationOverlay :is-open="isApplicationOverlayOpen"
                                   @close="toggleApplicationJourneyOverlay"
        >
            <FinanceApplication :total-cost-of-credit="quote.totalCostOfCredit"
                                :total-funded="quote.totalFunded"
                                :term="params.term"
                                :deposit="params.deposit"
                                :total-monthly="quote.totalMonthly"
                                :finance-type="params.financeType"
                                :utm-campaign="utmCampaign"
                                :utm-medium="utmMedium"
                                :utm-source="utmSource"
                                @close="toggleApplicationJourneyOverlay"
            />
        </FinanceApplicationOverlay>
    </div>
</template>
<script>
import FinanceCalculatorRange from './FinanceCalculatorRange.vue'
import FinanceCalculatorRow from './FinanceCalculatorRow.vue'
import FinanceCalculatorSelect from './FinanceCalculatorSelect.vue'
import FinanceCalculatorApprovedMessage from './FinanceCalculatorApprovedMessage.vue'
import FinanceCalculatorQuote from './FinanceCalculatorQuote.vue'
import FinanceApplicationOverlay from './FinanceApplicationOverlay.vue'
import FinanceApplication from './FinanceApplication.vue'
import FinanceCalculatorQuoteLoading from './FinanceCalculatorQuoteLoading.vue'
import FinanceCalculatorNotification from './FinanceCalculatorNotification.vue'

export default {
    components: {
        FinanceCalculatorNotification,
        FinanceApplication,
        FinanceApplicationOverlay,
        FinanceCalculatorQuote,
        FinanceCalculatorApprovedMessage,
        FinanceCalculatorSelect,
        FinanceCalculatorRow,
        FinanceCalculatorRange,
        FinanceCalculatorQuoteLoading,
    },
    props: {
        financeType: {
            type: String,
            default: 'car'
        },
        price: {
            type: Number,
            default: 8990
        },
        deposit: {
            type: Number,
            default: 0
        },
        term: {
            type: Number,
            default: 48
        },
        creditScore: {
            type: String,
            default: 'Tier1'
        },
        utmCampaign: {
            type: String
        },
        utmMedium: {
            type: String
        },
        utmSource: {
            type: String
        }
    },
    data() {
        return {
            params: {
                financeType: this.financeType,
                price: this.price,
                deposit: this.deposit,
                term: this.term,
                creditScore: this.creditScore,
            },
            quote: {
                rate: null,
                totalRepayment: null,
                totalCostOfCredit: null,
                totalFunded: null,
                term: null,
                totalMonthly: null,
            },
            isFetching: false,
            isInitiallyFetched: false,
            isApplicationOverlayOpen: false,
            isError: false
        }
    },
    created() {
        this.fetchQuote()
    },
    methods: {
        async fetchQuote() {
            this.isError = false
            this.isFetching = true

            const params = {
                vehicle_price: this.params.price,
                vehicle_type: this.params.financeType,
                term: this.params.term,
                deposit: this.params.deposit,
                credit_rating: this.params.creditScore
            }

            const csrfToken = document.head.querySelector('meta[name="csrf"]')

            try {
                const res = await fetch('/modules/FinanceCalculator/controller/get_example_vehicle_finance.php?' + new URLSearchParams({
                    ...params
                }), {
                        headers: {
                            'X-CSRF-TOKEN': csrfToken ? csrfToken.content : '' 
                        }
                    })

                const body = await res.json()

                if (!body.success) {
                    return await Promise.reject(body)
                }

                const { apr, payable_amount, amount_of_credit, term, monthly_payment_rate, cash_price } = body.cheapest_quote
                this.quote.rate =  apr
                this.quote.totalRepayment = payable_amount
                this.quote.totalCostOfCredit = Number(payable_amount - (cash_price - this.params.deposit))
                this.quote.totalFunded = Number(cash_price - this.params.deposit)
                this.quote.term = term
                this.quote.totalMonthly = monthly_payment_rate
            } catch (e) {
                this.isError = true
                console.error(e)
            } finally {
                if (!this.isInitiallyFetched) {
                    this.isInitiallyFetched = true
                }

                this.isFetching = false
            }
        },
        formatCurrency(value) {
            return new Intl.NumberFormat('en-GB', {
                style: 'currency',
                currency: 'GBP',
                maximumFractionDigits: 0,
                minimumFractionDigits: 0,
            }).format(value);
        },
        formatTerms(value) {
            return value + ' months'
        },
        toggleApplicationJourneyOverlay() {
            this.isApplicationOverlayOpen = !this.isApplicationOverlayOpen

            if (!this.isApplicationOverlayOpen) {
                history.pushState({}, '', '/finance')
            }
        }
    },
    watch: {
        params: {
            deep: true,
            handler() {
                this.fetchQuote()
            }
        }
    },
    computed: {
        financeOptions() {
            return [
                {
                    value: 'car',
                    label: 'Car'
                },
                {
                    value: 'van',
                    label: 'Van'
                }
            ]
        },
        creditScoreOptions() {
            return [
                {
                    value: 'Tier1',
                    label: 'Excellent'
                },
                {
                    value: 'Tier2',
                    label: 'Good'
                },
                {
                    value: 'Tier3',
                    label: 'Fair'
                },
                {
                    value: 'Tier4',
                    label: 'Below Average'
                }
            ]
        }
    },
}
</script>
